import React, { useEffect } from 'react';
import styled from '@emotion/styled';

import { CategoryType, ContentOrderType, MIN_CONTENT_SEARCH_LENGTH } from '@diritto/common/content/constants';
import { useAuthState } from 'contexts/AuthProvider';
import { SearchContentList } from 'app-components/search/SearchContentList';
import { useGetRelaySearchResultsInfiniteQuery } from 'queries/relay/useGetRelaySearchResultsInfiniteQuery';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { Loading } from 'ui-components/common/Loading';
import { EmptyList } from '@/ui-components/common/EmptyList';

type ContentSearchResultsWrapperProps = {
  keyword: string;
  setTotalCount: (cnt: number) => void;
  categoryType?: CategoryType;
  contentOrderType?: ContentOrderType;
  onlySerialized?: boolean;
};

export const ContentSearchResultsWrapper = ({
  keyword,
  setTotalCount,
  categoryType,
  contentOrderType,
  onlySerialized,
}: ContentSearchResultsWrapperProps) => {
  const { includeAdult } = useAuthState();
  const { data, hasNextPage, fetchNextPage, isLoading } = useGetRelaySearchResultsInfiniteQuery(keyword, {
    includeAdult,
    categoryType,
    contentOrderType,
    showOnlySerialized: onlySerialized,
  });

  useEffect(() => {
    if (data?.pages) {
      setTotalCount(data.pages[0].totalCount);
    }
  }, [data, setTotalCount]);

  if (keyword.length < MIN_CONTENT_SEARCH_LENGTH) {
    setTotalCount(0);
    return <EmptyList description="검색어는 두 글자 이상 입력해주세요." />;
  }

  return (
    <StyledContentList>
      {isLoading ? (
        <Loading />
      ) : (
        <StyledInfiniteScrollWrapper loadMore={fetchNextPage} hasNext={hasNextPage}>
          {data?.pages.map(({ contentIds, contents, profileLites }) => (
            <SearchContentList
              key={contentIds.join(',')}
              contentIds={contentIds}
              contents={contents}
              profileLites={profileLites}
              searchKeyword={keyword}
              numOfTotalSearchResults={data.pages[0].totalCount}
              emptyText={
                categoryType !== CategoryType.All || onlySerialized
                  ? '선택하신 조건에 맞는 작품이 없어요.'
                  : '검색 결과가 없어요.'
              }
            />
          ))}
        </StyledInfiniteScrollWrapper>
      )}
    </StyledContentList>
  );
};

const StyledContentList = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 50vh;

  > * {
    margin-top: 40px;

    &:first-of-type {
      margin-top: 0;
    }
  }
`;

const StyledInfiniteScrollWrapper = styled(InfiniteScrollWrapper)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
